import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import {
	ImageSizeTypeEnum,
	InvalidProductReasonEnum,
	ThemeBreakpointIndexEnum,
	ThemeFontStyleTypeEnum
} from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import type { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import type {
	IBundleProduct,
	IBundleShoppingProduct,
	ILocalizedInformation,
	IProduct,
	IShoppingProduct,
	IStore,
	IStoreAvailability,
	ITheme,
	IZipAvailability
} from '@inter-ikea-kompis/types';
import {
	AvailabilityUtility,
	DexfSettingsUtility,
	PriceUtility,
	ProductDescriptionProductMeasureEnum,
	ProductDescriptionUtility,
	ProductUtility,
	TotalPriceCalculator,
	ZipAvailabilityUtility
} from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Collapsible } from '@inter-ikea-kompis/component-collapsible';
import { DoNotDiy } from '@inter-ikea-kompis/component-do-not-diy';
import { Image } from '@inter-ikea-kompis/component-image';
import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { PriceModuleLayoutTypeEnum } from '@inter-ikea-kompis/component-price-module';
import { PriceModule } from '@inter-ikea-kompis/component-price-module';
import { Quantity } from '@inter-ikea-kompis/component-quantity';
import { Text } from '@inter-ikea-kompis/component-text';
import { TextToggleLayoutEnum } from '@inter-ikea-kompis/component-text-toggle';
import { TextToggle } from '@inter-ikea-kompis/component-text-toggle';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { CheckboxGroup as SkapaCheckboxGroup } from '@ingka/checkbox-webc/passive.js';
import { plusSmall, registerIcon, trashCan, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import type { InlineMessageVariant } from '@ingka/inline-message-webc';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { ProductIdentifier as SkapaProductIdentifier } from '@ingka/product-identifier-webc/passive.js';
import type { StatusColour } from '@ingka/status-webc';
import { Status as SkapaStatus } from '@ingka/status-webc/passive.js';

import ProductRowLayoutEnum from '../../enums/ProductRowLayoutEnum.js';
import type ProductRowModalEnum from '../../enums/ProductRowModalEnum.js';
import ProductRowOptionalStateEnum from '../../enums/ProductRowOptionalStateEnum.js';
import ProductRowProductLinkEnum from '../../enums/ProductRowProductLinkEnum.js';
import ProductRowProductMeasureEnum from '../../enums/ProductRowProductMeasureEnum.js';
import type IProductRowDexfSettings from '../../types/IProductRowDexfSettings.js';
import type IProductRowTranslations from '../../types/IProductRowTranslations.js';
import ProductRowUtility from '../../utilities/ProductRowUtility.js';
import CompactProductRow from '../compact-product-row/CompactProductRow.js';
import ProductRowStyle from './ProductRowStyle.js';

/**
 * This component is used to list products together with quantity and a total price per row. The quantity can be set to be editable or fixed. It also includes the Price Module which makes it compliant to all legal requirements.
 *
 * The Product Row is typically used in the Product List component, and is therefore not recommended to use on its own.
 *
 * @event quantity Triggered when the quantity is changed.
 * @event expand Triggered when included shopping products gets expanded.
 * @event collect Triggered when a product is collected.
 * @event add Triggered when a product is added.
 * @event remove Triggered when a product is removed.
 * @event productImageClick Triggered when the user clicks on a product image.
 * @event productNameClick Triggered when the user clicks on a product name.
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. event.detail.type tells you the type
 */

export default class ProductRow extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Shopping product or bundle product.
	@property({ type: Object, required: true })
	public shoppingProduct!: IShoppingProduct | IBundleShoppingProduct;

	// Included shopping product.
	@property({ type: Object })
	public includedProducts: IProduct[] | null = null;

	// Store availabilities.
	@property({ type: Object })
	public storeAvailabilities: IStoreAvailability[] | null = null;

	// Online availabilities.
	@property({ type: Object })
	public zipAvailabilities: IZipAvailability[] | null = null;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductRowTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IProductRowDexfSettings | null = null;

	// Show product package weight info.
	@property({ type: Boolean })
	public showWeight = false;

	// Show status indicator on print. Has no effect if layout is set to ProductRowLayoutEnum.compactPrint.
	@property({ type: Boolean })
	public showStockStatusOnPrint = false;

	// Show product id.
	@property({ type: Boolean })
	public showItemNo = false;

	// Product measures.
	@property({ type: String })
	public productMeasures: ProductRowProductMeasureEnum = ProductRowProductMeasureEnum.none;

	// Enable to be able to edit the quantity.
	@property({ type: Boolean })
	public editableQuantity = false;

	// Show product package measures.
	@property({ type: Boolean })
	public showPackageMeasures = false;

	// Stores.
	@property({ type: Object })
	public stores: IStore[] | null = null;

	// Selected store id.
	@property({ type: String })
	public storeId: string | null = null;

	// Show text describing the color and/or material of the product.
	@property({ type: Boolean })
	public showDesignText = false;

	// Show total price.
	@property({ type: Boolean })
	public showTotalPrice = false;

	// Recommended pattern for normal and kiosk mode, is eventOnly to open a ProductInformationCard in a Skapa sheet modal. If using productInformationPage, disable on kiosk.
	@property({ type: String })
	public productLink: ProductRowProductLinkEnum = ProductRowProductLinkEnum.productInformationPage;

	// Expands the included articles section.
	@property({ type: Boolean })
	public expanded = false;

	// Whether to show the collect checkbox or not.
	@property({ type: Boolean })
	public collectable = false;

	// A list of product ids that are collected.
	@property({ type: Object })
	public collected: string[] | null = null;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Optional product state
	@property({ type: String })
	public optionalState: ProductRowOptionalStateEnum = ProductRowOptionalStateEnum.mandatory;

	// Sets the minimum quantity value.
	@property({ type: Number })
	public quantityMinValue = 0;

	// Show a modal of type ProductCardModalEnum.
	@property({ type: String })
	public visibleModal: ProductRowModalEnum | null = null;

	// Layout for product row. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print.
	@property({ type: String })
	public layout: ProductRowLayoutEnum | null = null;

	// Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
	@property({ type: String })
	public measurementSystem: MeasurementSystemEnum | null = null;

	private breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate(), this);

	private dexfSettings: IProductRowDexfSettings | null = null;

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}
		return html`
			<style>
				${ProductRowStyle.getStyle({
					theme: this.theme,
					isMobile: this.isMobile(),
					optionalState: this.optionalState,
					showStockStatusOnPrint: this.showStockStatusOnPrint,
					hasDualCurrencies: this.isDualCurrency(),
					isDiscontinued: this.isDiscontinuedProduct(this.shoppingProduct.product),
					isSpr: ProductUtility.isProductSPR(this.shoppingProduct.product)
				})}
			</style>
			<div class="container" role="listitem">${this.getLayout()}</div>
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.breakpointObserver.disconnect();
	}
	public override async connectedCallback(): Promise<void> {
		registerIcon(trashCan);
		registerIcon(plusSmall);
		registerIcon(warningTriangle);

		this.customElements.define('skapa-status', SkapaStatus);
		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-checkbox-group', SkapaCheckboxGroup);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-product-identifier', SkapaProductIdentifier);
		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('kompis-price-formatter', PriceFormatter);
		this.customElements.define('kompis-quantity', Quantity);
		this.customElements.define('kompis-text-toggle', TextToggle);
		this.customElements.define('kompis-collapsible', Collapsible);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-image', Image);
		this.customElements.define('kompis-price-module', PriceModule);
		this.customElements.define('kompis-do-not-diy', DoNotDiy);
		this.customElements.define('kompis-compact-product-row', CompactProductRow);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		if (changedProperties.has('theme')) {
			this.breakpointObserver.observe(this.theme);
		}
		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getTranslations(): IProductRowTranslations {
		return this.translations
			? this.translations
			: <IProductRowTranslations>this.localizedInformation?.translations;
	}

	private getSettings(): IProductRowDexfSettings {
		return <IProductRowDexfSettings>this.dexfSettings;
	}

	private getLayout(): RenderResult {
		switch (this.layout) {
			case ProductRowLayoutEnum.compactPrint:
				return html`<kompis-compact-product-row
					.theme="${this.theme}"
					.shoppingProduct="${this.shoppingProduct}"
					.stores="${this.stores}"
					.storeId="${this.storeId}"
					.storeAvailabilities="${this.storeAvailabilities}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.settings="${this.settings}"
					.measurementSystem="${this.measurementSystem}"
				></kompis-compact-product-row>`;
			default:
				return html`${this.getMainProduct()} ${this.getIncludedProducts()}`;
		}
	}

	/**
	 * @returns Template.
	 */
	private getMainProduct(): RenderResult {
		const product = this.shoppingProduct.product;
		const isBundleProduct = ProductUtility.isBundleProduct(product);

		const storeAvailability = isBundleProduct
			? null
			: AvailabilityUtility.getStoreAvailabilityForProduct({
					product,
					storeAvailabilities: this.storeAvailabilities
				});
		const zipAvailability = isBundleProduct ? null : this.getZipAvailability(product);
		const isValid = product.valid;
		const mainProductCssClass = isValid ? 'mainProduct' : 'mainProduct discontinued';
		const rows: RenderResult[] = [];

		if (!product.content) {
			return null;
		}

		if (this.isMobile()) {
			rows.push(
				html`<div class="row" aria-describedby="${isValid ? '' : 'discontinuedProductDisclaimer'}">
					${this.getImage(product)}
					<div>
						${this.getUnitPrice(product)} ${this.getDoNotDiy(product)}
						${!this.shouldDisplayStoreAvailability(product)
							? this.getIdentifiersForProduct(product, null)
							: null}
						${this.getProductAvailabilityIndicators(product, zipAvailability, storeAvailability)}
					</div>
				</div>`
			);

			if (
				this.optionalState !== ProductRowOptionalStateEnum.addable &&
				this.optionalState !== ProductRowOptionalStateEnum.removableFromTab
			) {
				rows.push(
					html`<div class="row">
						<div class="quantityContainer">
							${this.getQuantityField(this.shoppingProduct)}
							${!this.isDiscontinuedProduct(product) ? this.getRemoveFromListButton() : null}
						</div>
						${this.getTotalPrice(this.shoppingProduct)}
					</div>`
				);
			}

			if (this.shouldDisplayStoreAvailability(product)) {
				rows.push(this.getIdentifiersForProduct(product, storeAvailability));
			}

			if (this.isCollectableProduct(product)) {
				rows.push(
					html`<div class="row">
						<div></div>
						${this.getCollectCheckbox(product)}
					</div>`
				);
			}

			if (
				this.optionalState !== ProductRowOptionalStateEnum.mandatory &&
				this.optionalState !== ProductRowOptionalStateEnum.removableFromList &&
				!this.isDiscontinuedProduct(product)
			) {
				rows.push(
					html`<div class="row">
						<div></div>
						${this.getAddButton()} ${this.getRemoveFromTabButton()}
					</div>`
				);
			}

			rows.push(this.getDiscontinuedProductDisclaimer(product));
		} else {
			rows.push(html`
				<div class="row" aria-describedby="${isValid ? '' : 'discontinuedProductDisclaimer'}">
					${this.getImage(product)}
					<div>
						${this.getUnitPrice(product)} ${this.getDoNotDiy(product)}
						${!this.shouldDisplayStoreAvailability(product)
							? this.getIdentifiersForProduct(product, null)
							: null}
						${this.getProductAvailabilityIndicators(product, zipAvailability, storeAvailability)}
					</div>
					${this.optionalState !== ProductRowOptionalStateEnum.addable &&
					this.optionalState !== ProductRowOptionalStateEnum.removableFromTab
						? html`<div>
								<div class="quantityContainer">
									${this.getQuantityField(this.shoppingProduct)}
									${!this.isDiscontinuedProduct(product) ? this.getRemoveFromListButton() : null}
								</div>
							</div>`
						: null}
					${!this.isDiscontinuedProduct(product) ? this.getTotalPrice(this.shoppingProduct) : null}
					${!this.isDiscontinuedProduct(product) ? this.getAddButton() : null}
					${!this.isDiscontinuedProduct(product) ? this.getRemoveFromTabButton() : null}
				</div>
			`);

			if (
				!this.isDiscontinuedProduct(product) &&
				(this.shouldDisplayStoreAvailability(product) || this.isCollectableProduct(product))
			) {
				rows.push(html`
					<div class="row">
						<div></div>
						<div>
							${this.shouldDisplayStoreAvailability(product)
								? this.getIdentifiersForProduct(product, storeAvailability)
								: null}
						</div>
						${this.optionalState !== ProductRowOptionalStateEnum.addable ? html`<div></div>` : null}
						${!this.isDiscontinuedProduct(product) ? this.getCollectCheckbox(product) : null}
					</div>
				`);
			}

			if (this.isDiscontinuedProduct(product)) {
				rows.push(html`
					<div class="row">
						<div></div>
						<div>${this.getDiscontinuedProductDisclaimer(product)}</div>
					</div>
				`);
			}
		}

		return html`<div class="${mainProductCssClass}">${rows}</div>`;
	}

	/**
	 * @returns Template.
	 */
	private getIncludedProducts(): RenderResult | null {
		if (!this.includedProducts) {
			return null;
		}

		const shoppingProducts = ProductUtility.getIncludedShoppingProducts({
			product: this.shoppingProduct.product,
			includedProducts: this.includedProducts
		});

		if (shoppingProducts.length === 0) {
			return null;
		}

		return html`
			<div class="includedProducts">
				<div
					class="includedProductsExpander"
					@keydown="${(event: KeyboardEvent) => event.key === 'Enter' && this.click()}"
					@click="${this.onIncludedProductsExpanderClick.bind(this)}"
				>
					<div></div>
					<kompis-text-toggle
						@expand="${() =>
							this.dispatchEvent(
								new CustomEvent('expand', { bubbles: true, detail: { expanded: !this.expanded } })
							)}"
						.theme="${this.theme}"
						.label="${this.getTranslations().productRowExpander}"
						.ariaLabel="${this.getTranslations()
							.includedArticlesNrOfArticles.replace(
								'[[nr_of_articles]]',
								String(shoppingProducts.length)
							)
							.replace('{{nr_of_articles}}', String(shoppingProducts.length))}"
						.printLabel="${this.getTranslations().productRowExpander +
						' (' +
						shoppingProducts.length +
						')'}"
						.expanded="${this.expanded}"
						.layout="${TextToggleLayoutEnum.subtle}"
						.size="${ThemeFontStyleTypeEnum.bodyMedium}"
					>
					</kompis-text-toggle>
				</div>
				<kompis-collapsible .expanded="${this.expanded}">
					<div class="spaceContainer">
						<div></div>
						${this.getA11yList(
							shoppingProducts.map((shoppingProduct) => this.getIncludedProduct(shoppingProduct))
						)}
					</div>
				</kompis-collapsible>
			</div>
		`;
	}

	/**
	 * @param listItems Content within a list.
	 */
	private getA11yList(listItems: RenderResult): RenderResult {
		/* eslint-disable lit-a11y/no-redundant-role */
		return html`
			<ul role="list">
				${listItems}
			</ul>
		`;
		/* eslint-enable lit-a11y/no-redundant-role */
	}

	private getProductAvailabilityIndicators(
		product: IProduct | IBundleProduct,
		zipAvailability: IZipAvailability | null,
		storeAvailability: IStoreAvailability | null
	): RenderResult {
		const zipAvailabilityIndicator = this.shouldDisplayZipAvailability(product)
			? this.getZipAvailabilityStatusIndicator(product, zipAvailability)
			: this.getZipAvailabilityStatusIndicator(product, null);

		const storeAvailabilityIndicator = this.getStoreAvailabilityStatusIndicator(
			product,
			storeAvailability
		);

		if (!zipAvailabilityIndicator && !storeAvailabilityIndicator) {
			return null;
		}

		return html`
			<div class="availabilityStatusIndicators">
				${zipAvailabilityIndicator}${storeAvailabilityIndicator}
			</div>
		`;
	}

	/**
	 * @param shoppingProduct Shopping product.
	 * @returns Template.
	 */
	private getIncludedProduct(shoppingProduct: IShoppingProduct): RenderResult {
		const product = shoppingProduct.product;

		if (!product.content) {
			return null;
		}

		const includedProductCssClass = product.valid
			? 'includedProduct'
			: 'includedProduct discontinued';

		const storeAvailability = AvailabilityUtility.getStoreAvailabilityForProduct({
			product,
			storeAvailabilities: this.storeAvailabilities
		});
		const zipAvailability = this.getZipAvailability(product);

		const rows: RenderResult = [];

		const parentIsSpr = ProductUtility.isProductSPR(this.shoppingProduct.product);

		if (this.isMobile()) {
			rows.push(html`
				<div class="row">
					${this.getProductContent(shoppingProduct)}
					${parentIsSpr
						? null
						: this.getProductAvailabilityIndicators(product, zipAvailability, storeAvailability)}
				</div>
			`);

			rows.push(html`
				<div class="row">${this.getIdentifiersForProduct(product, storeAvailability)}</div>
			`);

			if (this.isCollectableProduct(product)) {
				rows.push(html`<div class="row">${this.getCollectCheckbox(product)}</div>`);
			}

			if (this.isDiscontinuedProduct(product)) {
				rows.push(html`<div class="row">${this.getDiscontinuedProductDisclaimer(product)}</div> `);
			}
		} else {
			rows.push(html`
				<div class="row">
					<div>
						${this.getProductContent(shoppingProduct)}
						${parentIsSpr
							? null
							: this.getProductAvailabilityIndicators(product, zipAvailability, storeAvailability)}
					</div>
					<div></div>
				</div>
			`);

			rows.push(html`
				<div class="row">
					<div>${this.getIdentifiersForProduct(product, storeAvailability)}</div>
					${this.isCollectableProduct(product) ? this.getCollectCheckbox(product) : null}
				</div>
			`);

			if (this.isDiscontinuedProduct(product)) {
				rows.push(html`<div class="row">${this.getDiscontinuedProductDisclaimer(product)}</div> `);
			}
		}

		return html`<li class="${includedProductCssClass}">${rows}</li>`;
	}

	/**
	 * @param product Product.
	 * @returns Template.
	 */
	private getImage(product: IProduct | IBundleProduct): RenderResult {
		const isBundleProduct = ProductUtility.isBundleProduct(product);
		const productImage = ProductUtility.getMainImages(product).find(
			(productImage) => productImage.size === ImageSizeTypeEnum.S4
		);

		if (!productImage) {
			return html` <div class="productImage"></div> `;
		}

		if (this.isDiscontinuedProduct(product)) {
			return html`
				<kompis-image
					class="productImage"
					.src="${productImage.url}"
					.alt="${product?.content?.name}"
				></kompis-image>
			`;
		}

		if (!isBundleProduct && !this.disableLinks) {
			if (this.productLink === ProductRowProductLinkEnum.productInformationPage) {
				return html`
					<a
						@click="${this.onProductImageClick.bind(this, product)}"
						aria-hidden="true"
						class="productImage"
						href="${ProductUtility.getProductInformationPageLink(product, this.getSettings())}"
						tabindex="-1"
						target="_blank"
					>
						<kompis-image
							.src="${productImage.url}"
							.alt="${this.getProductImageAltText(product)}"
						></kompis-image>
					</a>
				`;
			} else if (this.productLink === ProductRowProductLinkEnum.eventOnly) {
				return html`
					<kompis-image
						class="productImage productImageEventOnly"
						@click="${this.onProductImageClick.bind(this, product)}"
						.src="${productImage.url}"
						.alt="${this.getProductImageAltText(product)}"
					></kompis-image>
				`;
			}
		}
		return html`
			<kompis-image
				class="productImage"
				.src="${productImage.url}"
				.alt="${this.getProductImageAltText(product)}"
			></kompis-image>
		`;
	}

	private getDoNotDiy(product: IProduct | IBundleProduct): RenderResult {
		if (ProductUtility.isBundleProduct(product)) {
			return null;
		}

		return html`<kompis-do-not-diy .product="${product}"></kompis-do-not-diy>`;
	}

	private getProductImageAltText(product: IProduct | IBundleProduct): string | null {
		if (!product.content?.name) {
			return null;
		}
		return this.getTranslations()
			.ariaLabelProductPageLink.replace('[[productName]]', product.content.name.toLowerCase())
			.replace('{{productName}}', product.content.name.toLowerCase());
	}

	/**
	 * @param product Product.
	 * @returns Template.
	 */
	private getDiscontinuedProductDisclaimer(product: IProduct | IBundleProduct): RenderResult {
		if (!this.isDiscontinuedProduct(product)) {
			return null;
		}

		const bodyText = this.getTranslations().discontinuedProductDisclaimer;
		const cautionaryVariant: InlineMessageVariant = 'cautionary';

		return html`<skapa-inline-message subtle variant="${cautionaryVariant}">
			<skapa-icon slot="message-icon" icon="warning-triangle"></skapa-icon>
			<span slot="body">${bodyText}</span>
		</skapa-inline-message>`;
	}

	/**
	 * @param shoppingProduct Shopping product.
	 * @returns Template.
	 */
	private getQuantityField(
		shoppingProduct: IShoppingProduct | IBundleShoppingProduct
	): RenderResult {
		if (
			this.optionalState === ProductRowOptionalStateEnum.addable ||
			this.optionalState === ProductRowOptionalStateEnum.removableFromTab
		) {
			return null;
		}

		const isValid = this.shoppingProduct.product.valid;

		if (!this.editableQuantity || !isValid) {
			return html`
				${ProductRowUtility.getQuantityField({
					shoppingProduct: this.shoppingProduct,
					translations: this.getTranslations()
				})}
			`;
		}

		return html`
			<div class="quantity">
				<kompis-quantity
					@change="${this.onQuantityInput.bind(this, shoppingProduct)}"
					.theme="${this.theme}"
					.min="${this.quantityMinValue}"
					.value="${shoppingProduct.quantity}"
				></kompis-quantity>
			</div>
		`;
	}

	/**
	 * @returns Template.
	 */
	private getAddButton(): RenderResult {
		if (this.optionalState !== ProductRowOptionalStateEnum.addable) {
			return null;
		}

		const secondaryVariant: ButtonVariant = 'secondary';

		if (this.isMobile()) {
			return html`
				<div class="addButton">
					<skapa-icon-button
						@click="${() => this.dispatchEvent(new CustomEvent('add', { bubbles: true }))}"
						variant="${secondaryVariant}"
						size="small"
					>
						${this.getTranslations().productListAddButton}
						<skapa-icon slot="icon" icon="plus-small"></skapa-icon>
					</skapa-icon-button>
				</div>
			`;
		}

		return html`
			<div class="addButton">
				<skapa-button
					@click="${() => this.dispatchEvent(new CustomEvent('add', { bubbles: true }))}"
					variant="${secondaryVariant}"
					size="small"
				>
					<skapa-icon icon="plus-small" slot="icon"></skapa-icon>
					${this.getTranslations().productListAddButton}
				</skapa-button>
			</div>
		`;
	}

	/**
	 * @returns Template.
	 */
	private getRemoveFromTabButton(): RenderResult {
		if (this.optionalState !== ProductRowOptionalStateEnum.removableFromTab) {
			return null;
		}

		const secondaryVariant: ButtonVariant = 'secondary';

		if (this.isMobile()) {
			return html`
				<div class="removeFromTabButton">
					<skapa-icon-button
						@click="${() => this.dispatchEvent(new CustomEvent('remove', { bubbles: true }))}"
						variant="${secondaryVariant}"
						size="small"
					>
						${this.getTranslations().productListRemoveButton}
						<skapa-icon slot="icon" icon="trash-can"></skapa-icon>
					</skapa-icon-button>
				</div>
			`;
		}

		return html`
			<div class="removeFromTabButton">
				<skapa-button
					@click="${() => this.dispatchEvent(new CustomEvent('remove', { bubbles: true }))}"
					variant="${secondaryVariant}"
					size="small"
				>
					<skapa-icon icon="trash-can" slot="icon"></skapa-icon>
					${this.getTranslations().productListRemoveButton}
				</skapa-button>
			</div>
		`;
	}

	/**
	 * @returns Template.
	 */
	private getRemoveFromListButton(): RenderResult {
		if (this.optionalState !== ProductRowOptionalStateEnum.removableFromList) {
			return null;
		}

		const tertiaryVariant: ButtonVariant = 'tertiary';

		return html`
			<div class="removeFromListButton">
				<skapa-icon-button
					@click="${() => this.dispatchEvent(new CustomEvent('remove', { bubbles: true }))}"
					variant="${tertiaryVariant}"
					size="small"
				>
					${this.getTranslations().productListRemoveButton}
					<skapa-icon slot="icon" icon="trash-can"></skapa-icon>
				</skapa-icon-button>
			</div>
		`;
	}

	/**
	 * @param shoppingProduct Shopping product.
	 * @returns Template.
	 */
	private getTotalPrice(shoppingProduct: IShoppingProduct | IBundleShoppingProduct): RenderResult {
		if (
			this.optionalState === ProductRowOptionalStateEnum.addable ||
			this.optionalState === ProductRowOptionalStateEnum.removableFromTab ||
			(!ProductUtility.isBundleProduct(shoppingProduct.product) &&
				!shoppingProduct.product.valid) ||
			!this.showTotalPrice ||
			this.isDiscontinuedProduct(this.shoppingProduct.product)
		) {
			return null;
		}

		const shoppingProducts = this.getPriceModuleShoppingProducts(shoppingProduct.product).map(
			(shoppingProduct) => ({
				...shoppingProduct,
				quantity: shoppingProduct.quantity * this.shoppingProduct.quantity
			})
		);

		const totalPrice = TotalPriceCalculator.calculateMultiple(
			shoppingProducts,
			this.getSettings().localisation.dateFormat,
			false
		);

		return html`
			<div class="totalPrice">
				${totalPrice.map((price, index) => {
					const isPrimaryCurrency = index < 1;
					return html`
						<kompis-text
							.theme="${this.theme}"
							.type="${isPrimaryCurrency
								? ThemeFontStyleTypeEnum.priceMedium
								: ThemeFontStyleTypeEnum.priceXSmall}"
							.bold="${true}"
						>
							<kompis-price-formatter
								.ariaLabelPrice="${'Total item price'}"
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${PriceUtility.getPrice(price)}"
								.mixedSize="${isPrimaryCurrency ? true : false}"
								.currencySymbol="${PriceUtility.getCurrencySymbol(price, this.getSettings())}"
							></kompis-price-formatter>
						</kompis-text>
					`;
				})}
			</div>
		`;
	}

	private getPriceModuleShoppingProducts(product: IProduct | IBundleProduct): IShoppingProduct[] {
		if (ProductUtility.shouldCalculatePriceFromChildProducts(product)) {
			const includedShoppingProducts = ProductUtility.getIncludedShoppingProducts({
				product,
				includedProducts: this.includedProducts ?? [],
				ignoreInvalidProducts: true
			});

			if (
				includedShoppingProducts.length > 0 &&
				includedShoppingProducts.length === product.content?.child?.length
			) {
				return includedShoppingProducts;
			}
		}

		return [{ quantity: 1, product: <IProduct>product }];
	}

	/**
	 * @param product Product.
	 * @returns Template.
	 */
	private getCollectCheckbox(product: IProduct | IBundleProduct): RenderResult {
		if (!this.isCollectableProduct(product)) {
			return null;
		}

		const { productCheckboxCollected, productCheckboxCollect } = this.getTranslations();

		const isCollected = this.isCollected(product);
		const checkboxId = `product-${product.itemId}`;

		return html`
			<div class="collect">
				<div class="checkboxWrapper">
					<label class="checkboxLabel" for="${checkboxId}">
						${isCollected ? productCheckboxCollected : productCheckboxCollect}
					</label>
					<skapa-checkbox-group>
						<input
							type="checkbox"
							aria-checked="${isCollected}"
							id="${checkboxId}"
							.checked="${isCollected}"
							@change="${this.onCollectCheckboxChange.bind(this, product)}"
						/>
					</skapa-checkbox-group>
				</div>
			</div>
		`;
	}

	/**
	 * Returns the article number and where to get the product inside the store.
	 * Where to get the product could either be aisle & bin, a department or "contact coworker".
	 *
	 * @param product Product.
	 * @param storeAvailability Store availability.
	 * @returns Template.
	 */
	private getIdentifiersForProduct(
		product: IProduct | IBundleProduct,
		storeAvailability: IStoreAvailability | null
	): RenderResult {
		if (
			(!this.storeAvailabilities && !this.showItemNo) ||
			ProductUtility.isBundleProduct(product)
		) {
			return null;
		}

		const isSpr = ProductUtility.isProductSPR(product);
		const displayStoreAvailability = this.shouldDisplayStoreAvailability(product);
		const withStoreAvailability = displayStoreAvailability ? storeAvailability : null;

		const { content, valid } = product;

		const articleNumber = this.showItemNo
			? ProductRowUtility.getArticleNumber({
					product,
					isSubtle: isSpr,
					showLabel: displayStoreAvailability,
					translations: this.getTranslations()
				})
			: null;

		const locationCode = ProductRowUtility.getLocation({
			storeAvailability: withStoreAvailability,
			displayStoreAvailability,
			translations: this.getTranslations()
		});

		const productIdentifiers = html`<div class="productIdentifiers">
			${articleNumber}
			<div class="productIdentifierDivider"></div>
			${locationCode}
		</div>`;

		if (!valid || !content || ProductUtility.isProductSPR(product)) {
			return productIdentifiers;
		}

		return html`
			<kompis-collapsible .expanded="${!this.isCollected(product)}">
				${productIdentifiers}
			</kompis-collapsible>
		`;
	}

	/**
	 * @param product Product.
	 * @param storeAvailability Store availability.
	 * @returns Template.
	 */
	private getStoreAvailabilityStatusIndicator(
		product: IProduct | IBundleProduct,
		storeAvailability: IStoreAvailability | null
	): RenderResult {
		const store = this.stores?.find((store) => store.id === this.storeId) || null;
		if (
			ProductUtility.isBundleProduct(product) ||
			!storeAvailability ||
			!product.valid ||
			!product.content ||
			!this.storeId ||
			!this.stores ||
			!store
		) {
			return null;
		}

		const statusColor = AvailabilityUtility.getStoreAvailabilityStatusColor(storeAvailability);
		if (!statusColor) {
			return null;
		}

		const statusLabel = AvailabilityUtility.getStoreAvailabilityLabel({
			translations: this.getTranslations(),
			storeAvailability
		})
			?.replace('[[store]]', store.name ?? '')
			.replace('{{store}}', store.name ?? '');

		return html`
			<skapa-status small colour="${statusColor}" class="text--body-s">${statusLabel}</skapa-status>
		`;
	}

	/**
	 * @param product Product.
	 * @param zipAvailability Zip Availability.
	 * @returns Template.
	 */
	private getZipAvailabilityStatusIndicator(
		product: IProduct | IBundleProduct,
		zipAvailability: IZipAvailability | null
	): RenderResult | null {
		if (ProductUtility.isBundleProduct(product) || !product.valid || !product.content) {
			return null;
		}

		if (!this.zipAvailabilities) {
			return null;
		}

		return this.getStatusIndicator(
			ZipAvailabilityUtility.getLabel({
				translations: this.getTranslations(),
				zipAvailability
			}),
			ZipAvailabilityUtility.getColor({
				zipAvailability
			})
		);
	}

	/**
	 * @param statusLabel ...
	 * @param statusColor ...
	 * @returns Template.
	 */
	private getStatusIndicator(statusLabel: string, statusColor: StatusColour | null): RenderResult {
		if (!statusColor) {
			return null;
		}

		return html`<skapa-status small colour="${statusColor}" class="text--body-s">
			${statusLabel}
		</skapa-status>`;
	}

	/**
	 * @param product Product.
	 * @returns Template.
	 */
	private getUnitPrice(product: IProduct | IBundleProduct): RenderResult {
		if (!this.hasTranslationsAndSettings()) {
			return null;
		}

		if (!product.valid) {
			return html`
				<div>
					<p
						class="productName notranslate text--body-m"
						aria-describedby="discontinuedProductDisclaimer"
						aria-label="${this.getProductDescription(product)}"
					>
						<span>${product.content?.name}</span>
					</p>
					<p class="productDescription text--body-m">
						<span>${this.getProductDescription(product)}</span>
					</p>
				</div>
			`;
		}

		const shoppingProducts = this.getPriceModuleShoppingProducts(product);
		const isNonPIAProducts = ProductUtility.shouldCalculatePriceFromChildProducts(product);

		return html`
			<kompis-price-module
				@modalOpen="${(event: CustomEvent) =>
					this.dispatchEvent(
						new CustomEvent('modalOpen', { bubbles: true, detail: event.detail })
					)}"
				@modalClose="${(event: CustomEvent) =>
					this.dispatchEvent(
						new CustomEvent('modalClose', { bubbles: true, detail: event.detail })
					)}"
				@productNameClick="${this.onProductNameClick.bind(this, product)}"
				.shoppingProducts="${shoppingProducts}"
				.settings="${this.settings}"
				.translations="${this.translations}"
				.localizedInformation="${this.localizedInformation}"
				.theme="${this.theme}"
				.layout="${PriceModuleLayoutTypeEnum.rowPrice}"
				.productName="${product.content?.name}"
				.showEnergyLabel="${!isNonPIAProducts}"
				.showRepairabilityIndex="${!isNonPIAProducts}"
				.visibleModal="${this.visibleModal}"
				.disableLinks="${this.disableLinks}"
				.productLink="${this.productLink}"
				.showPriceUnit="${!isNonPIAProducts}"
				.showEcoFee="${!isNonPIAProducts}"
				.showLowestPreviousSalesPrice="${!isNonPIAProducts}"
				disableHeadings
				.showOfferMessage="${!isNonPIAProducts}"
				><span>${this.getProductDescription(product)}</span></kompis-price-module
			>
		`;
	}

	/**
	 * This function wraps the name and description because of A11y, making it easier to hit the link to PIP.
	 *
	 * @param shoppingProduct ShoppingProduct.
	 */
	private getProductContent(shoppingProduct: IShoppingProduct): RenderResult {
		const content = html`
			${this.getVisuallyHiddenProductPageText(shoppingProduct)}
			${this.getIncludedProductName(shoppingProduct)}
			${this.getIncludedProductDescription(shoppingProduct)}
		`;

		if (
			this.productLink === ProductRowProductLinkEnum.productInformationPage &&
			!this.disableLinks
		) {
			return html`
				<a
					class="productContent"
					target="_blank"
					@click="${this.onProductNameClick.bind(this, shoppingProduct.product)}"
					href="${ProductUtility.getProductInformationPageLink(
						shoppingProduct.product,
						this.getSettings()
					)}"
				>
					${content}
				</a>
			`;
		} else if (this.productLink === ProductRowProductLinkEnum.eventOnly && !this.disableLinks) {
			return html`
				<div
					tabindex="0"
					@click="${this.onProductNameClick.bind(this, shoppingProduct.product)}"
					@keydown="${(event: KeyboardEvent) => {
						if (event.key === 'Enter') {
							this.onProductNameClick(shoppingProduct.product);
						}
					}}"
					class="productContent"
				>
					${content}
				</div>
			`;
		}

		return content;
	}

	private getVisuallyHiddenProductPageText(shoppingProduct: IShoppingProduct): RenderResult {
		const productName = shoppingProduct?.product?.content?.name;
		if (!productName) {
			return null;
		}

		const { ariaLabelProductPageLink, quantity } = this.getTranslations();
		const text =
			ariaLabelProductPageLink
				.replace('[[productName]]', productName.toLowerCase())
				.replace('{{productName}}', productName.toLowerCase()) +
			'. ' +
			quantity +
			' ' +
			shoppingProduct.quantity +
			'.';

		return html` <span class="visuallyHidden">${text}</span> `;
	}

	/**
	 * @param shoppingProduct Shopping product.
	 * @returns Template.
	 */
	private getIncludedProductName(shoppingProduct: IShoppingProduct): RenderResult {
		if (!shoppingProduct.product.content) {
			return null;
		}

		const text = `${shoppingProduct.quantity}x ${shoppingProduct.product.content.name}`;

		return html` <p class="productName notranslate text--body-m" aria-hidden="true">${text}</p> `;
	}

	/**
	 * Returns product description.
	 *
	 * @param product ...
	 * @returns Product description.
	 */
	private getProductDescription(product: IProduct | IBundleProduct): string | null {
		if (!this.hasTranslationsAndSettings()) {
			return null;
		}

		const description = ProductDescriptionUtility.getDescription({
			settings: this.getSettings(),
			translations: this.getTranslations(),
			product,
			include: {
				productMeasures: this.getProductMeasures(),
				designText: this.showDesignText,
				packageMeasures: this.showPackageMeasures,
				weight: this.showWeight,
				measurementSystem: this.measurementSystem
			}
		});

		return description;
	}

	/**
	 * @param shoppingProduct Shopping product.
	 * @returns Template.
	 */
	private getIncludedProductDescription(shoppingProduct: IShoppingProduct): RenderResult {
		return html`
			<div class="productDescription">
				<p class="text--body-m">
					${ProductDescriptionUtility.getDescription({
						settings: this.getSettings(),
						translations: this.getTranslations(),
						product: shoppingProduct.product,
						include: {
							productMeasures: this.getProductMeasures(),
							designText: this.showDesignText,
							packageMeasures: this.showPackageMeasures,
							weight: this.showWeight,
							measurementSystem: this.measurementSystem
						}
					})}
				</p>
			</div>
		`;
	}

	/**
	 * Returns product measures with a type that can be used with ProductDescriptionUtility.
	 *
	 * @returns Product measures.
	 */
	private getProductMeasures(): ProductDescriptionProductMeasureEnum {
		switch (this.productMeasures) {
			case ProductRowProductMeasureEnum.long:
				return ProductDescriptionProductMeasureEnum.long;
			case ProductRowProductMeasureEnum.short:
				return ProductDescriptionProductMeasureEnum.short;
			default:
			case ProductRowProductMeasureEnum.none:
				return ProductDescriptionProductMeasureEnum.none;
		}
	}

	/**
	 * @param shoppingProduct Shopping product.
	 * @param event Event.
	 */
	private onQuantityInput(
		shoppingProduct: IShoppingProduct | IBundleShoppingProduct,
		event: CustomEvent
	): void {
		this.dispatchEvent(
			new CustomEvent('quantity', {
				bubbles: true,
				detail: {
					shoppingProduct: {
						...shoppingProduct,
						quantity: event.detail.value
					}
				}
			})
		);
	}

	/**
	 * @param product Product.
	 */
	private onCollectCheckboxChange(product: IProduct): void {
		const collected = (this.collected || []).slice();
		const collectId = this.getCollectId(product);

		if (collected.includes(collectId)) {
			const index = collected.indexOf(collectId);
			if (index !== -1) {
				collected.splice(index, 1);
			}
		} else {
			collected.push(collectId);
		}
		this.dispatchEvent(
			new CustomEvent('collect', {
				bubbles: true,
				detail: {
					collected,
					collectId
				}
			})
		);
	}

	/**
	 * @param product Product.
	 */
	private onProductImageClick(product: IProduct): void {
		if (this.productLink !== ProductRowProductLinkEnum.disabled) {
			this.dispatchEvent(
				new CustomEvent('productImageClick', {
					bubbles: true,
					detail: {
						itemNo: product.content?.ruItemNo,
						productId: this.getCollectId(product)
					}
				})
			);
		}
	}

	/**
	 * @param product Product.
	 */
	private onProductNameClick(product: IProduct): void {
		if (this.productLink !== ProductRowProductLinkEnum.disabled) {
			this.dispatchEvent(
				new CustomEvent('productNameClick', {
					bubbles: true,
					detail: {
						itemNo: product.content?.ruItemNo,
						productId: this.getCollectId(product)
					}
				})
			);
		}
	}

	private onIncludedProductsExpanderClick(event: CustomEvent): void {
		const includedProductsExpander = this.shadowRoot?.querySelector('.includedProductsExpander');

		if (event.target !== includedProductsExpander) {
			return;
		}

		this.dispatchEvent(
			new CustomEvent('expand', { bubbles: true, detail: { expanded: !this.expanded } })
		);
	}

	/**
	 * @param product Product.
	 * @returns "true" if collected.
	 */
	private isCollected(product: IProduct): boolean {
		return (this.collected || []).includes(this.getCollectId(product));
	}

	/**
	 * @param product Product.
	 * @returns "true" if collected.
	 */
	private isDiscontinuedProduct(product: IProduct | IBundleProduct): boolean {
		if (ProductUtility.isBundleProduct(product)) {
			return false;
		}
		if (!product.valid) {
			for (const reason of product.reason) {
				if (reason.code === InvalidProductReasonEnum.retailItemNotAvailable) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * @param product Product.
	 * @returns Collect ID.
	 */
	private getCollectId(product: IProduct): string {
		const parentItemNo = this.shoppingProduct.product.itemId;
		const childItemNo = product.itemId;
		return parentItemNo !== childItemNo ? `${parentItemNo}/${childItemNo}` : (parentItemNo ?? '');
	}

	/**
	 * @param product Product.
	 * @returns Zip availability.
	 */
	private getZipAvailability(product: IProduct): IZipAvailability | null {
		if (!product.content || !product.valid || !this.zipAvailabilities) {
			return null;
		}
		return (
			this.zipAvailabilities.find((zipAvailability) => {
				return (
					product.content?.itemNoLocal === zipAvailability.content.itemNo ||
					product.content?.itemNoGlobal === zipAvailability.content.itemNo
				);
			}) || null
		);
	}

	/**
	 * @returns Returns "true" if mobile.
	 */
	private isMobile(): boolean {
		return (
			(this.breakpointObserver.getBreakpointIndex() ?? ThemeBreakpointIndexEnum.small) <=
			ThemeBreakpointIndexEnum.small
		);
	}

	/**
	 * @returns Returns "true" if the shopping product has dual currencies.
	 */
	private isDualCurrency(): boolean {
		if (ProductUtility.isBundleProduct(this.shoppingProduct.product)) {
			return false;
		}
		return (
			new Set(
				this.shoppingProduct.product.content?.priceInformation?.salesPrice.map(
					(price) => price.currencyCode
				)
			).size > 1
		);
	}

	/**
	 * @param product Product.
	 * @returns Returns "true" if collectable.
	 */
	private isCollectableProduct(product: IProduct | IBundleProduct): product is IProduct {
		return (
			!ProductUtility.isBundleProduct(product) &&
			!ProductUtility.isProductSPR(product) &&
			!!this.collectable &&
			!!product.valid &&
			!!product.content &&
			this.optionalState !== ProductRowOptionalStateEnum.addable &&
			this.optionalState !== ProductRowOptionalStateEnum.removableFromTab
		);
	}

	private shouldDisplayStoreAvailability(product: IProduct | IBundleProduct): boolean {
		return (
			!ProductUtility.isBundleProduct(product) &&
			product.valid &&
			!this.isDiscontinuedProduct(product) &&
			!ProductUtility.isProductSPR(product) &&
			!!this.storeAvailabilities
		);
	}

	private shouldDisplayZipAvailability(product: IProduct | IBundleProduct): boolean {
		return (
			!ProductUtility.isBundleProduct(product) &&
			product.valid &&
			!this.isDiscontinuedProduct(product)
		);
	}
}
declare global {
	interface HTMLElementTagNameMap {
		'skapa-status': SkapaStatus;
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-checkbox-group': SkapaCheckboxGroup;
		'skapa-icon': SkapaIcon;
		'skapa-product-identifier': SkapaProductIdentifier;
		'skapa-inline-message': SkapaInlineMessage;
		'kompis-product-row': ProductRow;
	}
}
